import {
    Component,
    Input,
    Output,
    EventEmitter,
    forwardRef,
    HostBinding,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, catchError, takeUntil } from 'rxjs/operators';
import { LucideAngularModule, X, ChevronDown, LoaderCircle, Check } from 'lucide-angular';

export interface ComboBoxOption {
    label: string;
    value: any;
    disabled?: boolean;
}

export interface ApiSearchConfig {
    url: string;
    searchParam?: string;
    labelProperty?: string;
    valueProperty?: string;
    headers?: { [key: string]: string };
    method?: 'GET' | 'POST';
    bodyTemplate?: (searchTerm: string) => any;
    responseTransform?: (response: any) => any[];
}

@Component({
    selector: 'app-combo-box',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
    templateUrl: './combo-box.component.html',
    styleUrl: './combo-box.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ComboBoxComponent),
            multi: true,
        },
    ],
})
export class ComboBoxComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    @HostBinding('attr.id') hostId: null = null;
    @Input('id') inputId?: string;
    @Input() ariaLabel?: string;
    @Input() ariaLabelledby?: string;
    @Input() options: ComboBoxOption[] = [];
    @Input() placeholder: string = 'Select an option';
    @Input() multiSelect: boolean = false;
    @Input() searchable: boolean = true;
    @Input() apiSearchConfig?: ApiSearchConfig;
    @Input() debounceTime: number = 500;
    @Input() minSearchLength: number = 1;
    @Input() clearable: boolean = true;
    @Input() loading: boolean = false;
    @Input() maxHeight: string = '200px';
    @Input() noResultsText: string = 'No results found';
    @Input() loadingText: string = 'Loading...';
    @Input() allowTextWrap: boolean = false; // Allow text to wrap instead of using ellipsis
    @Input() showError: boolean = false; // Show error state styling
    @Input() inputClass: string = ''; // Custom CSS classes for the input element

    @Output() selectionChange = new EventEmitter<any>();
    @Output() searchChange = new EventEmitter<string>();

    @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild('dropdown', { static: false }) dropdown!: ElementRef<HTMLDivElement>;
    @ViewChild('comboTrigger', { static: false }) comboTrigger?: ElementRef<HTMLDivElement>;

    searchControl = new FormControl('');
    isOpen = false;
    filteredOptions: ComboBoxOption[] = [];
    selectedOptions: ComboBoxOption[] = [];
    isLoading = false;
    disabled: boolean = false; // Managed internally by setDisabledState

    private destroy$ = new Subject<void>();
    private onChange = (value: any) => {};
    private onTouched = () => {};
    private writtenValue: any;
    private hasWrittenValue = false;
    private scrollListener?: () => void;
    private resizeListener?: () => void;

    readonly X = X;
    readonly ChevronDown = ChevronDown;
    readonly LoaderCircle = LoaderCircle;
    readonly Check = Check;

    constructor(
        private http: HttpClient,
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.setupSearch();
        this.setupClickOutside();
        this.setupScrollAndResize();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['options']) {
            this.filteredOptions = [...this.options];
            // If there's a current search term, re-apply the filter
            const currentSearchTerm = this.searchControl.value;
            if (currentSearchTerm) {
                this.performLocalSearch(currentSearchTerm);
            }
            this.syncSelectionFromWrittenValue();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        // Clean up event listeners
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener, true);
        }
        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }
    }

    private setupScrollAndResize() {
        // Update dropdown position on scroll (capture phase to catch all scroll events)
        this.scrollListener = () => {
            if (this.isOpen) {
                this.positionDropdown();
            }
        };
        window.addEventListener('scroll', this.scrollListener, true);

        // Update dropdown position on window resize
        this.resizeListener = () => {
            if (this.isOpen) {
                this.positionDropdown();
            }
        };
        window.addEventListener('resize', this.resizeListener);
    }

    private setupSearch() {
        this.searchControl.valueChanges
            .pipe(debounceTime(this.debounceTime), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((searchTerm) => {
                this.handleSearch(searchTerm || '');
            });
    }

    private setupClickOutside() {
        document.addEventListener('click', this.onDocumentClick.bind(this));
    }

    private onDocumentClick(event: Event) {
        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.closeDropdown();
        }
    }

    private handleSearch(searchTerm: string) {
        this.searchChange.emit(searchTerm);

        if (this.apiSearchConfig) {
            this.performApiSearch(searchTerm);
        } else {
            this.performLocalSearch(searchTerm);
        }
    }

    private performLocalSearch(searchTerm: string) {
        if (!searchTerm) {
            this.filteredOptions = [...this.options];
            return;
        }

        this.filteredOptions = this.options.filter((option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }

    private performApiSearch(searchTerm: string) {
        console.log('Performing API search with term:', searchTerm);
        console.log('API config:', this.apiSearchConfig);

        if (!this.apiSearchConfig || searchTerm.length < this.minSearchLength) {
            console.log('Search cancelled - insufficient length or no config');
            this.filteredOptions = [];
            return;
        }

        this.isLoading = true;
        this.loading = true;

        const searchObservable = this.createApiSearchObservable(searchTerm);

        searchObservable
            .pipe(
                catchError((error) => {
                    console.error('API search error:', error);
                    console.error('Error details:', {
                        status: error.status,
                        statusText: error.statusText,
                        message: error.message,
                        url: error.url,
                    });
                    return of([]);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((results) => {
                console.log('API search results received:', results);
                this.filteredOptions = this.mapApiResults(results);
                console.log('Final filtered options:', this.filteredOptions);
                this.isLoading = false;
                this.loading = false;
            });
    }

    private createApiSearchObservable(searchTerm: string): Observable<any> {
        const config = this.apiSearchConfig!;
        const url = config.url;
        const headers = config.headers || {};

        if (config.method === 'POST') {
            const body = config.bodyTemplate
                ? config.bodyTemplate(searchTerm)
                : { [config.searchParam || 'search']: searchTerm };
            return this.http.post(url, body, { headers });
        } else {
            const params: any = {};
            params[config.searchParam || 'search'] = searchTerm;
            return this.http.get(url, { headers, params });
        }
    }

    private mapApiResults(results: any): ComboBoxOption[] {
        console.log('mapApiResults called with:', results);

        // Handle Laravel's paginated response format
        let dataArray: any[] = [];
        if (Array.isArray(results)) {
            dataArray = results;
        } else if (results && Array.isArray(results.data)) {
            // Laravel paginated response
            dataArray = results.data;
        } else if (results && results.data) {
            // Single item response
            dataArray = [results.data];
        } else {
            console.warn('Unexpected API response format:', results);
            return [];
        }

        const labelProp = this.apiSearchConfig?.labelProperty || 'label';
        const valueProp = this.apiSearchConfig?.valueProperty || 'value';

        const mappedResults = dataArray.map((item) => ({
            label: item[labelProp] || item.toString(),
            value: item[valueProp] !== undefined ? item[valueProp] : item,
            disabled: item.disabled || false,
        }));

        console.log('Mapped results:', mappedResults);
        return mappedResults;
    }

    toggleDropdown() {
        if (this.disabled) return;

        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            // Clear search when opening dropdown
            this.searchControl.setValue('');
            // Position dropdown for fixed positioning
            this.positionDropdown();
            // Focus search input if searchable
            if (this.searchable) {
                setTimeout(() => {
                    this.searchInput?.nativeElement.focus();
                }, 100);
            }
        }
        this.onTouched();
    }

    onLabelProxyFocus() {
        if (this.disabled) {
            return;
        }

        if (!this.isOpen) {
            this.isOpen = true;
            this.searchControl.setValue('');
            this.positionDropdown();
        }

        setTimeout(() => {
            if (this.searchable && this.searchInput?.nativeElement) {
                this.searchInput.nativeElement.focus();
                return;
            }

            this.comboTrigger?.nativeElement.focus();
        }, 0);

        this.onTouched();
    }

    private positionDropdown() {
        if (!this.dropdown) return;

        setTimeout(() => {
            const inputElement = this.elementRef.nativeElement.querySelector('.combo-box-input');
            if (!inputElement) return;

            const rect = inputElement.getBoundingClientRect();
            const dropdownElement = this.dropdown.nativeElement;

            // Temporarily make visible to measure actual rendered height (respects max-height)
            dropdownElement.style.visibility = 'hidden';
            dropdownElement.style.display = 'block';
            const dropdownHeight = dropdownElement.offsetHeight;
            dropdownElement.style.visibility = '';
            dropdownElement.style.display = '';

            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const gap = 4; // margin between input and dropdown

            // Position above if not enough space below but enough above
            if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                dropdownElement.style.top = `${rect.top - dropdownHeight - gap}px`;
                dropdownElement.classList.add('drop-up');
                dropdownElement.classList.remove('drop-down');
            } else {
                dropdownElement.style.top = `${rect.bottom + gap}px`;
                dropdownElement.classList.add('drop-down');
                dropdownElement.classList.remove('drop-up');
            }

            dropdownElement.style.left = `${rect.left}px`;
            dropdownElement.style.width = `${rect.width}px`;
        }, 0);
    }
    closeDropdown() {
        this.isOpen = false;
    }

    selectOption(option: ComboBoxOption) {
        if (this.disabled || option.disabled) return;

        if (this.multiSelect) {
            this.handleMultiSelect(option);
        } else {
            this.handleSingleSelect(option);
        }

        this.emitValue();
        this.selectionChange.emit(this.getValue());
    }

    private handleSingleSelect(option: ComboBoxOption) {
        this.selectedOptions = [option];
        this.searchControl.setValue(''); // Clear search after selection
        this.closeDropdown();
    }

    private handleMultiSelect(option: ComboBoxOption) {
        const index = this.selectedOptions.findIndex((selected) => this.compareValues(selected.value, option.value));

        if (index > -1) {
            this.selectedOptions.splice(index, 1);
        } else {
            this.selectedOptions.push(option);
        }

        // Don't close dropdown for multi-select and clear search
        this.searchControl.setValue('');
    }

    removeOption(option: ComboBoxOption, event?: Event) {
        if (this.disabled) {
            return;
        }
        if (event) {
            event.stopPropagation();
        }

        this.selectedOptions = this.selectedOptions.filter(
            (selected) => !this.compareValues(selected.value, option.value),
        );
        this.onChange(this.getValue());
        this.selectionChange.emit(this.getValue());
    }

    clearSelection(event?: Event) {
        if (this.disabled) {
            return;
        }
        if (event) {
            event.stopPropagation();
        }

        this.selectedOptions = [];
        this.onChange(this.getValue());
        this.selectionChange.emit(this.getValue());
    }

    isOptionSelected(option: ComboBoxOption): boolean {
        return this.selectedOptions.some((selected) => this.compareValues(selected.value, option.value));
    }

    private compareValues(a: any, b: any): boolean {
        if (a === b) {
            return true;
        }

        if (typeof a === 'object' || typeof b === 'object' || a == null || b == null) {
            if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
                return JSON.stringify(a) === JSON.stringify(b);
            }
            return false;
        }

        return String(a) === String(b);
    }

    private emitValue() {
        this.onChange(this.getValue());
    }

    private getValue() {
        if (this.multiSelect) {
            return this.selectedOptions.map((option) => option.value);
        } else {
            return this.selectedOptions.length > 0 ? this.selectedOptions[0].value : null;
        }
    }

    // ControlValueAccessor implementation
    writeValue(value: any): void {
        this.writtenValue = value;
        this.hasWrittenValue = true;
        this.syncSelectionFromWrittenValue(true);
    }

    private syncSelectionFromWrittenValue(clearSearchWhenEmpty = false): void {
        if (!this.hasWrittenValue) {
            return;
        }

        const value = this.writtenValue;

        if (value === undefined || value === null) {
            this.selectedOptions = [];
            if (clearSearchWhenEmpty) {
                this.searchControl.setValue('');
            }
            return;
        }

        if (this.multiSelect && Array.isArray(value)) {
            this.selectedOptions = this.findOptionsByValues(value);
            return;
        }

        if (!this.multiSelect) {
            const option = this.findOptionByValue(value);
            this.selectedOptions = option ? [option] : [];
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.searchControl.disable();
        } else {
            this.searchControl.enable();
        }
    }

    private findOptionByValue(value: any): ComboBoxOption | undefined {
        return this.options.find((option) => this.compareValues(option.value, value));
    }

    private findOptionsByValues(values: any[]): ComboBoxOption[] {
        return values.map((value) => this.findOptionByValue(value)).filter((option) => option) as ComboBoxOption[];
    }

    getDisplayText(): string {
        if (this.selectedOptions.length === 0) {
            return this.placeholder;
        }

        if (this.multiSelect) {
            if (this.selectedOptions.length === 1) {
                return this.selectedOptions[0].label;
            }
            return `${this.selectedOptions.length} items selected`;
        } else {
            return this.selectedOptions[0].label;
        }
    }

    trackByValue(index: number, option: ComboBoxOption): any {
        return option.value;
    }
}
